import { useCurrencyContext } from '@apeiron/currency'
import { StarLite } from '@apeiron/star'
import { NextLink, SkeletonBox, SkeletonImage } from '@apeiron/temp'
import { ApeironNumberTextField } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import { DATE_DISPLAY_FORMAT_WITHOUT_TIME } from '@src/constants/date'
import { ROUTE } from '@src/constants/route'
import {
  OrbitalTrackDetachDetail,
  OrbitalTrackSetting
} from '@src/types/orbitalTrack'
import { addDays, format } from 'date-fns'
import queryString from 'query-string'
import * as R from 'ramda'
import { FC, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const Title = styled(Typography)`
  color: white;
  font-weight: 400;
  margin-top: -40px;
  ${props => props.theme.breakpoints.up('res1280')} {
    font-size: 24px;
    line-height: 30px;
    padding: 40px 55px 10px 38px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    font-size: 20px;
    line-height: 26px;
    padding: 40px 20px 0px 20px;
  }
`

const UpperContainer = styled(Box)`
  border-bottom: 2px dashed rgba(128, 128, 126, 0.5);
  display: flex;
  ${props => props.theme.breakpoints.up('res1280')} {
    gap: 24px;
    padding: 0 55px 24px 38px;
  }
  ${props => props.theme.breakpoints.between('res768', 'res1280')} {
    gap: 15px;
    padding: 10px 10px 10px 20px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    align-items: center;
    flex-direction: column;
    padding: 10px 10px 10px 20px;
  }
`

const StarCard = styled(SkeletonImage)`
  aspect-ratio: 0.686;
  ${props => props.theme.breakpoints.up('res1280')} {
    align-self: flex-start;
    width: 145px;
  }
  ${props => props.theme.breakpoints.between('res768', 'res1280')} {
    align-self: flex-start;
    width: 136px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    width: 136px;
  }
`

const UpperVerticalContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  padding: 4px 0;
`

const RentalDetail = styled(Typography)`
  color: #2181e2;
  font-weight: 700;
  ${props => props.theme.breakpoints.up('res1280')} {
    font-size: 20px;
    line-height: 24px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    font-size: 20px;
    line-height: 22px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    align-self: center;
  }
`

const RowContainer = styled(Box)`
  align-items: center;
  display: flex;
  gap: 10px;
  ${props => props.theme.breakpoints.up('res480')} {
    justify-content: flex-start;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    align-items: flex-start;
    flex-direction: column;
  }
`

const RentalTitle = styled(Typography)`
  color: #ffffff;
  font-weight: 400;
  &.disabled {
    color: #6c6e6e;
  }
  ${props => props.theme.breakpoints.up('res1280')} {
    font-size: 16px;
    line-height: 24px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    font-size: 16px;
    line-height: 20px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    align-self: center;
  }
`

const RentalTitleWithRemark = styled(RentalTitle)`
  ${props => props.theme.breakpoints.up('res1280')} {
    margin-bottom: 16px;
  }
  &.disabled {
    margin-bottom: unset;
  }
`

const ValueContainer = styled(SkeletonBox)`
  width: 150px;
  ${props => props.theme.breakpoints.up('res480')} {
    margin-left: auto;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    align-self: center;
  }
`

const RentalValue = styled(ApeironNumberTextField)`
  pointer-events: none;
  width: 100%;
  .MuiOutlinedInput-input {
    padding: 2px 0px;
    -webkit-text-fill-color: unset;
  }
  &.disabled {
    opacity: 0.4;
    .MuiOutlinedInput-input {
      color: transparent;
    }
    .MuiInputAdornment-root,
    .MuiFormHelperText-root {
      display: none;
    }
  }
`

const Remark = styled(Typography)`
  color: #80807e;
`

const AgingBuffValue = styled(RentalValue)`
  .MuiOutlinedInput-input {
    color: #8ee161;
  }
`

const RenewalValue = styled(RentalValue)`
  .MuiOutlinedInput-input {
    color: #ffbf36;
  }
`

const OrbitalTrackOperatingStarInfo: FC<Props> = props => {
  const {
    isOwner,
    loading,
    settingOnRenting,
    setting,
    star,
    subtitle,
    title,
    isRenew = false
  } = props

  const { control, setValue } = useForm()

  const { t } = useTranslation()

  const { getApeironPrice, getCurrency } = useCurrencyContext()

  const disableClass = !isOwner ? {} : { className: 'disabled' }

  const rentEndDate = useMemo(() => {
    if (settingOnRenting) {
      return format(
        isRenew
          ? addDays(new Date(), settingOnRenting.rentalDuration)
          : new Date(settingOnRenting.contractEndTime),
        DATE_DISPLAY_FORMAT_WITHOUT_TIME
      )
    } else if (setting) {
      return format(
        addDays(new Date(), setting.rentalDuration),
        DATE_DISPLAY_FORMAT_WITHOUT_TIME
      )
    }

    return '-'
  }, [isRenew, setting, settingOnRenting])

  const url = useMemo(() => {
    return queryString.stringifyUrl({
      url: ROUTE.MARKETPLACE_STAR_DETAIL,
      query: { starId: star?.id }
    })
  }, [star?.id])

  useEffect(() => {
    if (R.isNotNil(star)) {
      const agingBuff = star.buff.agingBuff + star.additionBuff.agingBuff

      setValue('agingBuff', `+${agingBuff}`)
    }

    if (R.isNotNil(settingOnRenting)) {
      const renewal = settingOnRenting.canBeRenew
        ? t('common.yes')
        : t('common.no')

      const fee = getApeironPrice(
        settingOnRenting.currencyType,
        settingOnRenting.rentalFee
      )

      setValue('availableTracks', 0)
      setValue('occupiedTrack', 0)
      setValue('rentalDuration', settingOnRenting.rentalDuration)
      setValue('rentalFee', fee)
      setValue('renewal', renewal)
    } else if (R.isNotNil(setting)) {
      const renewal = setting.canBeRenew ? t('common.yes') : t('common.no')

      const fee = getApeironPrice(setting.currencyType, setting.rentalFee)

      setValue('availableTracks', setting.totalTrack - setting.occupiedTrack)
      setValue('occupiedTrack', setting.occupiedTrack)
      setValue('rentalDuration', setting.rentalDuration)
      setValue('rentalFee', fee)
      setValue('renewal', renewal)
    }
  }, [setting, settingOnRenting, star])

  const rentalFeeUnit = settingOnRenting
    ? getCurrency(settingOnRenting.currencyType)?.name
    : setting
    ? getCurrency(setting.currencyType)?.name
    : ''

  return (
    <>
      <Title>{title}</Title>
      <UpperContainer>
        <NextLink href={url} target='_blank'>
          <StarCard src={star?.image || ''} />
        </NextLink>
        <UpperVerticalContainer>
          <RentalDetail>{subtitle}</RentalDetail>
          <RowContainer>
            <RentalTitle>
              {t('godiverse.orbital_track.attach_planet.aging_buff')}
            </RentalTitle>
            <ValueContainer loading={loading}>
              <AgingBuffValue control={control} name={'agingBuff'} />
            </ValueContainer>
          </RowContainer>
          <RowContainer>
            <RentalTitle {...disableClass}>
              {t('godiverse.orbital_track.available')}
            </RentalTitle>
            <ValueContainer loading={loading}>
              <RentalValue
                control={control}
                name={'availableTracks'}
                {...disableClass}
              />
            </ValueContainer>
          </RowContainer>
          <RowContainer>
            <RentalTitle {...disableClass}>
              {t('godiverse.orbital_track.locked')}
            </RentalTitle>
            <ValueContainer loading={loading}>
              <RentalValue
                control={control}
                name={'occupiedTrack'}
                {...disableClass}
              />
            </ValueContainer>
          </RowContainer>
          <RowContainer>
            <RentalTitle {...disableClass}>
              {t('godiverse.orbital_track.attach_planet.rental_fee')}
            </RentalTitle>
            <ValueContainer loading={loading}>
              <RentalValue
                control={control}
                name={'rentalFee'}
                unit={rentalFeeUnit}
                {...disableClass}
              />
            </ValueContainer>
          </RowContainer>
          <RowContainer>
            <RentalTitleWithRemark {...disableClass}>
              {t('godiverse.orbital_track.attach_planet.rental_duration')}
            </RentalTitleWithRemark>
            <ValueContainer loading={loading}>
              <RentalValue
                control={control}
                name={'rentalDuration'}
                unit={t('godiverse.orbital_track.days')}
                remark={
                  <Remark as='span'>
                    {t('godiverse.orbital_track.rental_end_on', {
                      date: rentEndDate
                    })}
                  </Remark>
                }
                {...disableClass}
              />
            </ValueContainer>
          </RowContainer>
          <RowContainer>
            <RentalTitleWithRemark {...disableClass}>
              {t('godiverse.orbital_track.attach_planet.allow_renewal')}
            </RentalTitleWithRemark>
            <ValueContainer loading={loading}>
              <RenewalValue
                control={control}
                name={'renewal'}
                {...disableClass}
              />
            </ValueContainer>
          </RowContainer>
        </UpperVerticalContainer>
      </UpperContainer>
    </>
  )
}

type Props = {
  isOwner: boolean
  loading: boolean
  settingOnRenting?: OrbitalTrackDetachDetail | null
  setting?: OrbitalTrackSetting | null
  star: StarLite | null
  subtitle: string
  title: string
  isRenew?: boolean
}

export default OrbitalTrackOperatingStarInfo
